import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import styles from './AddASecondFor.module.scss';
import Checkbox from '../Checkbox';
import AnimatedVariantSelection from '../AnimatedVariantSelection';
import {
  findVariantBySku,
  getVariantsWithPercentDiscount,
  findVariantAttributeByName,
  filterVariantsByAttributeNameAndKey,
  findVariantByAttributeValues,
} from '../../../utils/variants';

const AddASecondFor = ({
  id,
  preAddedLabel,
  postAddedLabel,
  isChecked,
  onChange,
  variants,
  attributeNameToFilter = undefined,
  percentDiscount,
  primarySelectedSKU,
  onSelectVariant,
  isShowingPriceDetails = true,
  isShowingDiscountedPrices = false,
  attributesExtraInfo = [],
  attributesUpperTitle = undefined,
  productName = undefined,
}) => {
  const [secondarySku, setSecondarySku] = useState();

  const primaryVariantsWithDiscount = useMemo(
    () => getVariantsWithPercentDiscount(variants, percentDiscount),
    [variants, percentDiscount],
  );

  const { secondaryVariants, primaryAttributes } = useMemo(() => {
    const firstVariantSelected =
      findVariantBySku({
        variants: primaryVariantsWithDiscount,
        sku: primarySelectedSKU,
      }) ?? primaryVariantsWithDiscount[0];
    const attributeKeyToFilter = findVariantAttributeByName(
      firstVariantSelected,
      attributeNameToFilter,
    )?.key;

    return {
      secondaryVariants: !attributeKeyToFilter
        ? primaryVariantsWithDiscount
        : filterVariantsByAttributeNameAndKey({
          variants: primaryVariantsWithDiscount,
          name: attributeNameToFilter,
          key: attributeKeyToFilter,
        }),
      primaryAttributes: firstVariantSelected.attributes,
    };
  }, [attributeNameToFilter, primarySelectedSKU, primaryVariantsWithDiscount]);

  useEffect(() => {
    setSecondarySku((prevSku) => {
      if (!isChecked) {
        return primarySelectedSKU;
      }

      const primaryAttribute = findVariantAttributeByName(
        { attributes: primaryAttributes },
        attributeNameToFilter,
      );
      const secondVariantSelected =
        findVariantBySku({
          variants: primaryVariantsWithDiscount,
          sku: prevSku,
        }) ??
        findVariantBySku({
          variants: primaryVariantsWithDiscount,
          sku: primarySelectedSKU,
        });

      const newAttributesToSearch = secondVariantSelected.attributes.map(
        (attr) =>
          (attr.name !== attributeNameToFilter ? attr : primaryAttribute),
      );

      return (
        findVariantByAttributeValues({
          variants: primaryVariantsWithDiscount,
          attributeValues: newAttributesToSearch,
        })?.sku ?? primarySelectedSKU
      );
    });
  }, [
    isChecked,
    secondaryVariants,
    primaryAttributes,
    primaryVariantsWithDiscount,
    primarySelectedSKU,
    attributeNameToFilter,
  ]);

  return (
    <>
      <div className={styles['second-product-checkbox-container']}>
        <Checkbox
          name={id}
          id={id}
          onChange={onChange}
          checked={isChecked}
          label={(
            <div className={styles['second-product-checkbox-label']}>
              {isChecked ? postAddedLabel : preAddedLabel}
            </div>
          )}
        />

        {isChecked && (
          <AnimatedVariantSelection
            isShowing={isChecked}
            variants={secondaryVariants}
            defaultSelectedSKU={secondarySku}
            onSelectVariant={(skuObject) => {
              setSecondarySku(skuObject.sku);
              onSelectVariant(skuObject);
            }}
            isShowingPriceDetails={isShowingPriceDetails}
            isShowingDiscountedPrices={isShowingDiscountedPrices}
            attributesExtraInfo={attributesExtraInfo}
            attributesUpperTitle={attributesUpperTitle}
            productName={productName}
          />
        )}
      </div>
    </>
  );
};

AddASecondFor.propTypes = {
  id: PropTypes.string.isRequired,
  preAddedLabel: PropTypes.string.isRequired,
  postAddedLabel: PropTypes.string.isRequired,
  isChecked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  variants: PropTypes.arrayOf(PropTypes.object).isRequired,
  attributeNameToFilter: PropTypes.string,
  percentDiscount: PropTypes.number.isRequired,
  primarySelectedSKU: PropTypes.string.isRequired,
  onSelectVariant: PropTypes.func.isRequired,
  isShowingPriceDetails: PropTypes.bool,
  isShowingDiscountedPrices: PropTypes.bool,
  attributesExtraInfo: PropTypes.arrayOf(
    PropTypes.shape({
      attributeName: PropTypes.string.isRequired,
      isToolTip: PropTypes.bool.isRequired,
      text: PropTypes.string,
      content: PropTypes.string.isRequired,
    }),
  ),
  attributesUpperTitle: PropTypes.string,
  productName: PropTypes.string,
};

export default AddASecondFor;
